import { BASE_URL } from "./URLs";

const LOCAL_UPLOAD_PREFIXES = [
  "/opt/getbetter/mongodb/uploads",
  "/uploads",
];

const PUBLIC_UPLOAD_MOUNT = "/api/uploads";

function normalizeSlashes(p) {
  return String(p || "").replace(/\\/g, "/");
}

export function isLocalUploadPath(value) {
  if (!value || typeof value !== "string") return false;
  const s = normalizeSlashes(value).trim();
  if (!s || /^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) {
    return false;
  }
  if (s === PUBLIC_UPLOAD_MOUNT || s.startsWith(PUBLIC_UPLOAD_MOUNT + "/")) return true;
  return LOCAL_UPLOAD_PREFIXES.some(
    (prefix) => s === prefix || s.startsWith(prefix + "/")
  );
}

function getUploadRelativeKey(value) {
  if (!value || typeof value !== "string") return null;
  let s = normalizeSlashes(value).trim().split("#")[0].split("?")[0];

  if (/^https?:\/\//i.test(s)) {
    try {
      s = new URL(s).pathname || "";
    } catch {
      return null;
    }
  }

  // /api_new/api/uploads/foo or /api/uploads/foo
  const mountIdx = s.indexOf(PUBLIC_UPLOAD_MOUNT + "/");
  if (mountIdx >= 0) {
    return s.slice(mountIdx + PUBLIC_UPLOAD_MOUNT.length + 1).replace(/^\/+/, "");
  }

  for (const prefix of LOCAL_UPLOAD_PREFIXES) {
    if (s.startsWith(prefix + "/")) {
      return s.slice(prefix.length + 1).replace(/^\/+/, "");
    }
  }
  return null;
}

/**
 * Convert DB local path → full API uploads URL.
 * /opt/getbetter/mongodb/uploads/profile-pictures/a.jpg
 * → https://getbettertransportation.com/api_new/api/uploads/profile-pictures/a.jpg
 */
export function resolveMediaUrl(value) {
  if (!value || typeof value !== "string") return value;

  // Already absolute http(s) cloud / CDN — leave as-is (unless it's our uploads URL)
  if (/^https?:\/\//i.test(value) && !value.includes("/api/uploads/") && !isLocalUploadPath(value)) {
    return value;
  }

  const key = getUploadRelativeKey(value);
  if (!key || key.includes("..")) return value;

  const apiBase = String(BASE_URL || "").replace(/\/+$/, "");
  if (!apiBase) return `${PUBLIC_UPLOAD_MOUNT}/${key}`;
  return `${apiBase}/uploads/${key}`;
}

/**
 * Display URL for profile/docs:
 * - local upload path → /api/uploads/...
 * - http(s) → as-is (or resolve if uploads URL)
 * - legacy filename only → `${imagesBase}/${filename}`
 */
export function getDisplayUrl(value, imagesBase) {
  if (!value || typeof value !== "string") return value || "";
  const s = value.trim();
  if (!s) return "";
  if (isLocalUploadPath(s) || s.includes("/api/uploads/")) return resolveMediaUrl(s);
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
  if (imagesBase) return `${String(imagesBase).replace(/\/+$/, "")}/${s}`;
  return s;
}

/** Deep-rewrite local upload paths inside API JSON payloads. */
export function rewriteLocalPathsInData(data) {
  if (typeof data === "string") {
    return isLocalUploadPath(data) ? resolveMediaUrl(data) : data;
  }
  if (Array.isArray(data)) {
    return data.map(rewriteLocalPathsInData);
  }
  if (data && typeof data === "object") {
    if (typeof Blob !== "undefined" && data instanceof Blob) return data;
    if (typeof File !== "undefined" && data instanceof File) return data;
    if (typeof ArrayBuffer !== "undefined" && data instanceof ArrayBuffer) return data;
    const out = {};
    for (const key of Object.keys(data)) {
      out[key] = rewriteLocalPathsInData(data[key]);
    }
    return out;
  }
  return data;
}
